<template>
  <section class="admin-content" v-if="auth_user">

    <!-- ── Page Header ── -->
    <div class="page-header">
      <div class="container">
        <div class="d-flex align-items-center text-white" style="padding: 32px 0; gap: 16px;">
          <div class="header-icon-wrap" style="background: rgba(255,255,255,0.15);">
            <i class="fe fe-check-circle" style="font-size: 22px;"></i>
          </div>
          <div class="flex-grow-1">
            <h4 class="mb-0 font-weight-bold">Registration Successful</h4>
            <small style="opacity: .8;">{{ auth_user.firstname }} {{ auth_user.lastname }}</small>
          </div>
          <button class="btn btn-outline-light btn-sm" @click="printMe">
            <i class="fe fe-printer mr-1"></i> Print Slip
          </button>
        </div>
      </div>
    </div>

    <div class="container mt-n3 pb-5">

      <!-- ── Success Banner ── -->
      <div class="card shadow mb-4" style="border-left: 4px solid #10b981; border-radius: 12px;">
        <div class="card-body py-4 px-4">
          <div class="d-flex align-items-start" style="gap: 14px;">
            <div style="width: 40px; height: 40px; border-radius: 50%; background: #d1fae5; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
              <i class="fe fe-check text-success" style="font-size: 18px;"></i>
            </div>
            <div>
              <p class="font-weight-bold mb-1">Congratulations on your successful registration!</p>
              <p class="text-muted mb-1" style="font-size: .88rem;">Your ID Card will be available in <strong>3 weeks</strong>. You can walk up to our office to collect it.</p>
              <p class="mb-0 text-muted" style="font-size: .85rem;">
                For enquiries: <a href="mailto:info.claimify.ng" class="text-primary">info.claimify.ng</a>
                &nbsp;&middot;&nbsp;
                <a href="tel:0700022556447" class="text-primary">0700022556447</a>
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Registration Slip (printable) ── -->
      <div class="card shadow table-card mb-4" id="printDiv">
        <div class="form-card-header">
          <div class="section-number"><i class="fe fe-file-text" style="font-size: 12px;"></i></div>
          <h6 class="mb-0 font-weight-bold text-white">Registration Slip</h6>
        </div>

        <div class="card-body py-4">
          <div class="row">

            <!-- Photo -->
            <div class="col-md-3 text-center mb-4 mb-md-0">
              <img
                :src="`https://claimify-api.hayokmedicare.ng/image/${auth_user.user_image}`"
                alt="User Photo"
                onerror="this.onerror=null; this.src='/assets/img/placeholder.svg'"
                class="rounded"
                style="width: 140px; height: 160px; object-fit: cover; border: 3px solid #2563eb; box-shadow: 0 4px 12px rgba(37,99,235,0.12);"
              />
              <div class="mt-2">
                <span class="badge badge-soft-primary px-3 py-1" style="border-radius: 20px; font-size: .78rem;">
                  {{ auth_user.plan_type }}
                </span>
              </div>
            </div>

            <!-- Details -->
            <div class="col-md-9">
              <ul class="list-unstyled mb-0">
                <li class="info-row">
                  <i class="fe fe-user info-icon"></i>
                  <div>
                    <p class="info-label">Full Name</p>
                    <p class="info-value mb-0">{{ auth_user.lastname }} {{ auth_user.firstname }} {{ auth_user.middlename }}</p>
                  </div>
                </li>
                <li class="info-row">
                  <i class="fe fe-hash info-icon"></i>
                  <div>
                    <p class="info-label">Insurance ID</p>
                    <p class="info-value mb-0 font-weight-bold text-primary" style="letter-spacing: .06em;">{{ auth_user.id_card_number }}</p>
                  </div>
                </li>
                <li class="info-row">
                  <i class="fe fe-home info-icon"></i>
                  <div>
                    <p class="info-label">Facility to Access Care</p>
                    <p class="info-value mb-0">{{ auth_user.userprovider && auth_user.userprovider.agency_name }}</p>
                  </div>
                </li>
                <li class="info-row">
                  <i class="fe fe-phone info-icon"></i>
                  <div>
                    <p class="info-label">Phone Number</p>
                    <p class="info-value mb-0">{{ auth_user.phone_number }}</p>
                  </div>
                </li>
                <li class="info-row" v-if="auth_user.email">
                  <i class="fe fe-mail info-icon"></i>
                  <div>
                    <p class="info-label">Email</p>
                    <p class="info-value mb-0">{{ auth_user.email }}</p>
                  </div>
                </li>
                <li class="info-row">
                  <i class="fe fe-calendar info-icon"></i>
                  <div>
                    <p class="info-label">Date of Birth</p>
                    <p class="info-value mb-0">{{ auth_user.dob | moment("dddd, MMMM Do YYYY") }}</p>
                  </div>
                </li>
                <li class="info-row">
                  <i class="fe fe-users info-icon"></i>
                  <div>
                    <p class="info-label">Gender</p>
                    <p class="info-value mb-0">{{ auth_user.gender }}</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Dependents -->
        <div v-if="auth_user.dependents && auth_user.dependents.length" class="border-top">
          <div class="d-flex align-items-center px-4 pt-3 pb-2">
            <i class="fe fe-users text-primary mr-2"></i>
            <h6 class="mb-0 font-weight-bold">
              Dependents
              <span class="badge badge-soft-info ml-1">{{ auth_user.dependents.length }}</span>
            </h6>
          </div>
          <div class="table-responsive">
            <table class="table mb-0">
              <thead class="table-head">
                <tr>
                  <th class="table-th">#</th>
                  <th class="table-th">Full Name</th>
                  <th class="table-th">Relationship</th>
                  <th class="table-th">Gender</th>
                  <th class="table-th">Date of Birth</th>
                  <th class="table-th">Expiry Date</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(dep, index) in auth_user.dependents" :key="dep.id" class="table-row">
                  <td class="py-2 px-4">{{ index + 1 }}</td>
                  <td class="py-2">{{ dep.full_name }}</td>
                  <td class="py-2">{{ dep.relationShipType }}</td>
                  <td class="py-2">{{ dep.gender }}</td>
                  <td class="py-2">{{ dep.dob | moment("MMM Do YYYY") }}</td>
                  <td class="py-2">{{ dep.expiry_date | moment("MMM Do YYYY") }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- ── Actions ── -->
      <div class="d-flex flex-wrap" style="gap: 8px;">
        <button class="btn btn-submit px-4" @click="printMe">
          <i class="fe fe-printer mr-1"></i> Print Registration Slip
        </button>
        <a href="https://www.claimify.ng" class="btn btn-outline-secondary px-4">
          <i class="fe fe-globe mr-1"></i> Go to Website
        </a>
      </div>

    </div>

    <div class="vld-parent">
      <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
    </div>
  </section>
</template>

<script>
// Import component
import Loading from "vue-loading-overlay";
// Import stylesheet
import "vue-loading-overlay/dist/vue-loading.css";
// Init plugin
// import { WebCam } from "vue-web-cam";

export default {
  components: {
    Loading,
  },
  data() {
    return {
      isLoading: false,
      fullPage: true,
      auth_user: null,
      dependent: "",
    };
  },
  mounted() {
    //
  },
  beforeMount() {
    this.user = JSON.parse(localStorage.getItem("user"));
    this.axios
      .get(`/api/v1/user-no-auth/${this.$route.params.id}`)
      .then((response) => {
        this.auth_user = response.data.user;
        console.log(response);
      })
      .catch((error) => {
        console.error(error);
      });
  },

  methods: {
    printMe() {
      let printContents = document.getElementById("printDiv").innerHTML;
      let originalContents = document.body.innerHTML;
      document.body.innerHTML = printContents;
      window.print();
      document.body.innerHTML = originalContents;
    },
  },
  created() {
    //
  },
};
</script>
<style scoped>
.page-header {
  background: linear-gradient(135deg, #065f46 0%, #10b981 100%);
}
</style>
